import React, { useEffect } from "react";
import { EmotionSchema } from "../../types/schemas";
import { useConversation } from "../../hooks/useConversation";
import { Reward } from "./Reward";
import { Options } from "./Options";
import { CharacterModal } from "./CharacterModal";
import { uppercaseFirstLetter } from "../../utils/uppercaseFirstLetter";
import { MessageSwitch } from "./MessageSwitch";
import { ConversationVariable, Reward as RewardType } from "../../types/types";
import { SplashScreen } from "./SplashScreen";
import { VoteOptions } from "./VoteOptions";
import { PollSlideType } from "../../../../../swiper-carousel/slide/poll";
import { Meter } from "./Meter";
import { getLoveMeterValue } from "../../../../../../clients/poll";
import { Loading } from "../../../../../loading";
import { usePollContext } from "../../../../../../stores/poll";

export function Dialog({
    modalStyles,
    characterName,
    conversationName,
    lang,
    getLocalization,
    handleLinkClick,
    trackButtonClick,
    pollSlideOptions,
    pollId,
    toggleVariables,
    variablesRef,
}: {
    modalStyles: React.CSSProperties;
    characterName: string;
    conversationName: string;
    closeCustomModal: () => void;
    lang: string;
    getLocalization: (id: string) => string;
    handleLinkClick: (cta: InboxCTA) => void;
    trackButtonClick: (
        id: string,
        label?: string,
        additionalInformation?: Record<string, unknown>,
    ) => void;
    pollSlideOptions?: PollSlideType["options"];
    pollId?: string;
    toggleVariables: (variables: { name: string; value: string }[]) => void;
    variablesRef: React.RefObject<ConversationVariable[]>;
}) {
    const {
        messages,
        availableQuestions,
        currentMessageIndex,
        currentPartIndex,
        allBubblesVisible,
        selectQuestion,
        advanceToNextPart,
        conversation,
        lastSelectedQuestion,
        loading,
        poll,
    } = useConversation({
        lang,
        conversationName,
        toggleVariables,
        variablesRef,
    });
    const pollContext = usePollContext();

    const {
        isClosed,
        actions: { refreshPollInfo },
    } = pollContext;
    const [loveMeterValue, setLoveMeterValue] = React.useState<number>(0);
    useEffect(() => {
        refreshPollInfo(pollId || "");
    }, [pollId]);

    const emotion =
        variablesRef.current?.find(
            (v) => v.name === `${uppercaseFirstLetter(characterName)}Emotion`,
        )?.value ?? "Neutral";

    const validatedEmotionData = EmotionSchema.safeParse(emotion);
    if (!validatedEmotionData.success) {
        console.warn(
            `Invalid emotion "${emotion}" for character "${characterName}", defaulting to "Neutral".`,
        );
    }
    const validatedEmotion = validatedEmotionData.success
        ? validatedEmotionData.data
        : "Neutral";

    const previousPartWasReward =
        messages[currentMessageIndex]?.body[currentPartIndex - 1]?.type ===
        "reward";

    const isFinalQuestion = pollId !== null && poll.options.length > 0;

    // Fetch love meter value when poll becomes available
    React.useEffect(() => {
        if (!pollId) {
            return;
        }

        getLoveMeterValue(pollId).then((value) => {
            if (value !== null) {
                setLoveMeterValue(value);
            }
        });
    }, [pollId]);

    const isWorstDate = variablesRef.current?.find(
        (v) => v.name === "SplashWorstDate" && v.value === "true",
    );

    const isPerfectDate = variablesRef.current?.find(
        (v) => v.name === "SplashPerfectDate" && v.value === "true",
    );

    const isSiriusHiddenReward = variablesRef.current?.find(
        (v) => v.name === "SiriusHiddenRewardFound" && v.value === "true",
    );

    const isClone = validatedEmotion === "Clone";

    return (
        <div
            className={`modal-overlay ${conversationName} ${lang} ${isFinalQuestion ? "final-question" : ""} ${isClone ? "clone" : ""} ${isWorstDate ? "worst-date" : ""} ${isPerfectDate ? "perfect-date" : ""} ${isSiriusHiddenReward ? "sirius-hidden-reward" : ""}`}
            style={modalStyles}
            onClick={() => {
                if (
                    conversation === null ||
                    conversation === undefined ||
                    loading
                )
                    return;

                if (!allBubblesVisible) {
                    advanceToNextPart();
                }
            }}
        >
            {conversation === null || conversation === undefined || loading ? (
                <Loading light />
            ) : (
                <>
                    {isFinalQuestion && (
                        <SplashScreen getLocalization={getLocalization} />
                    )}
                    <CharacterModal
                        conversationName={conversationName}
                        emotion={validatedEmotion}
                    />
                    {previousPartWasReward && (
                        <Reward
                            reward={
                                (
                                    messages[currentMessageIndex]?.body[
                                        currentPartIndex - 1
                                    ] as RewardType
                                ).name
                            }
                            donationId={
                                (
                                    messages[currentMessageIndex]?.body[
                                        currentPartIndex - 1
                                    ] as RewardType
                                ).donationId
                            }
                            trackButtonClick={trackButtonClick}
                            getLocalization={getLocalization}
                        />
                    )}
                    {isFinalQuestion && (
                        <Meter
                            oldValue={0}
                            newValue={loveMeterValue}
                            animationDuration={3000}
                            dividers={[33, 66]}
                            finished={isClosed(pollId || "")}
                        />
                    )}
                    <div className="modal-wrapper">
                        <div className="modal-content">
                            <div className="message-container">
                                <MessageSwitch
                                    messages={messages}
                                    currentMessageIndex={currentMessageIndex}
                                    currentPartIndex={currentPartIndex}
                                    conversationName={conversationName}
                                    handleLinkClick={handleLinkClick}
                                    getLocalization={getLocalization}
                                />
                            </div>
                            {!isFinalQuestion && allBubblesVisible && (
                                <Options
                                    visibleQuestions={availableQuestions}
                                    selectQuestion={selectQuestion}
                                    startSectionId={
                                        conversation.start.sectionId
                                    }
                                    getLocalization={getLocalization}
                                    trackButtonClick={trackButtonClick}
                                    lastSelectedQuestion={
                                        lastSelectedQuestion ?? {
                                            text: characterName,
                                            sectionId:
                                                conversation.start.sectionId,
                                        }
                                    }
                                />
                            )}
                            {isFinalQuestion && (
                                <VoteOptions
                                    poll={poll}
                                    pollId={pollId}
                                    pollSlideOptions={pollSlideOptions}
                                    getLocalization={getLocalization}
                                />
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
